/**
 * Product-page share menu (snippets/capucinne-share.liquid).
 *
 * One delegated listener on the document, so the markup may be rendered late
 * (quick-view, section re-render) without re-initialising anything.
 *
 *  - Facebook: the OFFICIAL Share button (Meta JS SDK, fb-share-button) is
 *    rendered inside the row the first time a menu opens; the row itself links
 *    to the official sharer URL, which is also the fallback when the SDK is
 *    blocked or slow.
 *  - Pinterest: the OFFICIAL Save button (pinit.js, buttonPin) the same way,
 *    with the pin-create URL as the row link / fallback.
 *  - Instagram: no web share endpoint exists. Wherever the browser offers a
 *    share sheet (navigator.share: phones, Chrome/Edge/Safari desktops) the row
 *    opens it (Instagram DM / WhatsApp / Messages ...); otherwise the row is a
 *    plain link to the store's Instagram profile. Nothing is copied.
 *  - Send to a friend: POSTs { to, from_name, message, product } to the Worker
 *    (/apps/capucinne-consultant/share through the Shopify app proxy). A
 *    { ok:false, fallback:'mailto' } answer (no email provider configured yet),
 *    any other failure, or a network error falls back to a pre-filled mailto:
 *    link so the shopper can still send it.
 *  - Copy link: clipboard API with the execCommand fallback.
 *
 * The SDKs are only loaded once a shopper opens the menu - nothing on page load.
 * Emits `capucinne:share` CustomEvents ({ network, outcome }) for analytics.
 */
(function () {
  'use strict';

  var ROOT_SEL = '[data-cap-share]';
  var OPEN_CLASS = 'is-open';
  var REQUEST_TIMEOUT_MS = 10000;
  var SDK_TIMEOUT_MS = 6000;
  var FB_SDK = 'https://connect.facebook.net/en_US/sdk.js';
  var FB_VERSION = 'v21.0';
  var PIN_SDK = 'https://assets.pinterest.com/js/pinit.js';

  function $(root, sel) { return root ? root.querySelector(sel) : null; }

  function emit(network, outcome, extra) {
    try {
      var detail = { network: network, outcome: outcome };
      if (extra) { for (var k in extra) { if (Object.prototype.hasOwnProperty.call(extra, k)) detail[k] = extra[k]; } }
      document.dispatchEvent(new CustomEvent('capucinne:share', { detail: detail }));
    } catch (error) { /* no CustomEvent */ }
  }

  function readProduct(root) {
    var d = root.dataset || {};
    var url = d.url || location.href.split('#')[0];
    var image = d.image || '';
    try { url = new URL(url, location.origin).toString(); } catch (error) { /* keep */ }
    try { if (image) image = new URL(image, location.origin).toString(); } catch (error) { /* keep */ }
    return {
      url: url,
      title: d.title || document.title,
      image: image,
      price: d.price || '',
      endpoint: d.endpoint || '/apps/capucinne-consultant/share',
      locale: d.locale || (document.documentElement.lang || '')
    };
  }

  // ---- status line ---------------------------------------------------------
  var statusTimers = new WeakMap();
  function setStatus(root, text, tone, sticky) {
    var el = $(root, '[data-cap-share-status]');
    if (!el) return;
    el.textContent = text || '';
    el.className = 'cap-share__status' + (tone ? ' is-' + tone : '') + (text ? ' is-visible' : '');
    var prev = statusTimers.get(root);
    if (prev) clearTimeout(prev);
    if (text && !sticky) {
      statusTimers.set(root, setTimeout(function () {
        if (el.textContent === text) { el.textContent = ''; el.className = 'cap-share__status'; }
      }, 4000));
    }
  }

  // ---- official buttons (loaded on first open) -----------------------------
  function loadScript(src, attrs) {
    return new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[src="' + src + '"]');
      if (existing) {
        if (existing.getAttribute('data-cap-loaded') === 'true') { resolve(); return; }
        existing.addEventListener('load', function () { resolve(); });
        existing.addEventListener('error', function () { reject(new Error('blocked')); });
        return;
      }
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.defer = true;
      s.crossOrigin = 'anonymous';
      if (attrs) { for (var k in attrs) { if (Object.prototype.hasOwnProperty.call(attrs, k)) s.setAttribute(k, attrs[k]); } }
      s.addEventListener('load', function () { s.setAttribute('data-cap-loaded', 'true'); resolve(); });
      s.addEventListener('error', function () { reject(new Error('blocked')); });
      document.head.appendChild(s);
    });
  }

  function withTimeout(promise, ms) {
    return new Promise(function (resolve, reject) {
      var timer = setTimeout(function () { reject(new Error('timeout')); }, ms);
      promise.then(function (v) { clearTimeout(timer); resolve(v); }, function (e) { clearTimeout(timer); reject(e); });
    });
  }

  var fbReady = null;
  function facebookSdk(appId) {
    if (fbReady) return fbReady;
    fbReady = new Promise(function (resolve, reject) {
      if (window.FB && window.FB.XFBML) { resolve(window.FB); return; }
      var previous = window.fbAsyncInit;
      window.fbAsyncInit = function () {
        try { if (typeof previous === 'function') previous(); } catch (error) { /* ignore */ }
        var init = { xfbml: false, version: FB_VERSION };
        if (appId) init.appId = appId; // theme setting "Meta App ID" - Meta's plugin needs it to render
        try { window.FB.init(init); } catch (error) { /* ignore */ }
        resolve(window.FB);
      };
      loadScript(FB_SDK + (appId ? '#appId=' + encodeURIComponent(appId) : '')).catch(reject);
    });
    return fbReady;
  }

  var pinReady = null;
  function pinterestSdk() {
    if (pinReady) return pinReady;
    pinReady = new Promise(function (resolve, reject) {
      if (typeof window.parsePinBtns === 'function') { resolve(window.parsePinBtns); return; }
      // pinit.js is a two-stage loader (it injects pinit_main.js), so the build
      // function shows up a moment after the first script's load event.
      loadScript(PIN_SDK, { 'data-pin-build': 'parsePinBtns', 'data-pin-hover': 'false' }).then(function () {
        var tries = 0;
        var tick = function () {
          if (typeof window.parsePinBtns === 'function') { resolve(window.parsePinBtns); return; }
          tries += 1;
          if (tries >= 24) { reject(new Error('no parsePinBtns')); return; }
          setTimeout(tick, 250);
        };
        tick();
      }).catch(reject);
    });
    return pinReady;
  }

  // The slot stays display:none until the SDK has produced its button (so a
  // blocked SDK never leaves an empty gap), hence presence, not size, decides.
  function officialRendered(slot, selector) {
    return !!slot.querySelector(selector);
  }

  function markOfficial(root, network, ok) {
    var wrap = $(root, '[data-cap-share-official="' + network + '"]');
    if (!wrap) return;
    wrap.classList.toggle('is-official', !!ok);
    wrap.classList.toggle('is-fallback', !ok);
    var row = wrap.closest('.cap-share__row');
    if (row) { row.classList.toggle('has-official', !!ok); } // hides the row link's own pill
    emit(network, ok ? 'official_button' : 'fallback_link');
  }

  function renderOfficialButtons(root) {
    var menu = menuOf(root);
    if (!menu || root.getAttribute('data-cap-share-official-state')) return;
    root.setAttribute('data-cap-share-official-state', 'loading');

    var fbWrap = $(root, '[data-cap-share-official="facebook"]');
    var appId = (root.dataset && root.dataset.facebookAppId) || '';
    if (fbWrap && !appId) {
      // Meta's share-button plugin draws a blank frame without an App ID (checked
      // live 2026-09-14), so without the "Meta App ID" theme setting the row keeps
      // the plain link to Facebook's official sharer and the SDK is never loaded.
      markOfficial(root, 'facebook', false);
    } else if (fbWrap) {
      withTimeout(facebookSdk(appId), SDK_TIMEOUT_MS).then(function (FB) {
        var slot = fbWrap.querySelector('.cap-share__official-slot');
        return new Promise(function (resolve) {
          // (the row link's "Share" pill hides once the official button exists - see markOfficial)
          try { FB.XFBML.parse(slot); } catch (error) { resolve(false); return; }
          // The SDK stamps fb-xfbml-state="rendered" on the plugin box once the
          // iframe is in place (the slot is display:none until then, so sizes
          // cannot be used). The SDK is only loaded with a Meta App ID, and with
          // one the rendered iframe is the real blue Share button.
          var tries = 0;
          var tick = function () {
            var box = slot.querySelector('.fb-share-button');
            if (box && box.getAttribute('fb-xfbml-state') === 'rendered' && slot.querySelector('iframe')) { resolve(true); return; }
            tries += 1;
            if (tries >= 20) { resolve(false); return; }
            setTimeout(tick, 250);
          };
          tick();
        });
      }).then(function (ok) { markOfficial(root, 'facebook', ok); })
        .catch(function () { markOfficial(root, 'facebook', false); });
    }

    var pinWrap = $(root, '[data-cap-share-official="pinterest"]');
    if (pinWrap) {
      withTimeout(pinterestSdk(), SDK_TIMEOUT_MS).then(function (parsePinBtns) {
        var slot = pinWrap.querySelector('.cap-share__official-slot');
        // pinit_main.js usually builds every pin-create link it finds on load; the
        // explicit parse only matters when the menu markup arrived later.
        if (!officialRendered(slot, '[data-pin-log], a[class*="PIN_"]')) {
          try { parsePinBtns(slot); } catch (error) { /* ignore */ }
        }
        return new Promise(function (resolve) {
          // pinit.js rebuilds the pin-create link in place as its official Save
          // button (class PIN_<stamp>_button_pin); poll briefly because the swap
          // is not always synchronous. When pinit is blocked the same link simply
          // stays a styled "Save" pill - one element, two looks.
          var tries = 0;
          var tick = function () {
            var built = officialRendered(slot, '[data-pin-log], a[class*="PIN_"]');
            if (built) { resolve(true); return; }
            tries += 1;
            if (tries >= 12) { resolve(false); return; }
            setTimeout(tick, 250);
          };
          tick();
        });
      }).then(function (ok) { markOfficial(root, 'pinterest', ok); })
        .catch(function () { markOfficial(root, 'pinterest', false); });
    }
    root.setAttribute('data-cap-share-official-state', 'done');
  }

  // ---- open / close --------------------------------------------------------
  function menuOf(root) { return $(root, '[data-cap-share-menu]'); }
  function triggerOf(root) { return $(root, '[data-cap-share-trigger]'); }

  function isOpen(root) { return root.classList.contains(OPEN_CLASS); }

  function closeMenu(root, restoreFocus) {
    if (!root || !isOpen(root)) return;
    var menu = menuOf(root);
    var trigger = triggerOf(root);
    root.classList.remove(OPEN_CLASS);
    if (menu) menu.hidden = true;
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    hideEmailForm(root);
    setStatus(root, '');
    if (restoreFocus && trigger) { try { trigger.focus({ preventScroll: true }); } catch (error) { trigger.focus(); } }
  }

  function closeAll(except) {
    var roots = document.querySelectorAll(ROOT_SEL);
    for (var i = 0; i < roots.length; i += 1) { if (roots[i] !== except) closeMenu(roots[i], false); }
  }

  function openMenu(root) {
    var menu = menuOf(root);
    var trigger = triggerOf(root);
    if (!menu) return;
    closeAll(root);
    root.classList.add(OPEN_CLASS);
    menu.hidden = false;
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    // Phones with a share sheet get the extra "More options…" row.
    var native = canNativeShare();
    var nativeRow = $(root, '[data-cap-share-native-row]');
    if (nativeRow) nativeRow.hidden = !native;
    var first = menu.querySelector('.cap-share__item');
    if (first) { try { first.focus({ preventScroll: true }); } catch (error) { first.focus(); } }
    renderOfficialButtons(root);
    emit('menu', 'open');
  }

  // ---- email form ----------------------------------------------------------
  function emailFormOf(root) { return $(root, '[data-cap-share-email]'); }
  function emailButtonOf(root) { return $(root, '[data-cap-share-network="email"]'); }

  function hideEmailForm(root) {
    var form = emailFormOf(root);
    var button = emailButtonOf(root);
    if (form) form.hidden = true;
    if (button) button.setAttribute('aria-expanded', 'false');
    root.classList.remove('is-emailing');
  }

  function showEmailForm(root) {
    var form = emailFormOf(root);
    var button = emailButtonOf(root);
    if (!form) return;
    form.hidden = false;
    if (button) button.setAttribute('aria-expanded', 'true');
    root.classList.add('is-emailing');
    var to = form.querySelector('input[name="to"]');
    if (to) { try { to.focus({ preventScroll: true }); } catch (error) { to.focus(); } }
  }

  function toggleEmailForm(root) {
    var form = emailFormOf(root);
    if (!form) return;
    if (form.hidden) showEmailForm(root); else hideEmailForm(root);
  }

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function mailtoFor(product, to, fromName, message) {
    var who = fromName ? fromName : 'A friend';
    var subject = who + ' thought you\'d love this piece from Capucinne';
    var lines = [];
    if (message) lines.push(message, '');
    lines.push(product.title + (product.price ? ' - ' + product.price : ''), product.url, '', 'Sent from capucinne.com');
    return 'mailto:' + encodeURIComponent(to) + '?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(lines.join('\n'));
  }

  function postShare(endpoint, body) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, REQUEST_TIMEOUT_MS) : null;
    return fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      credentials: 'same-origin',
      signal: controller ? controller.signal : undefined
    }).then(function (response) {
      return response.text().then(function (text) {
        var data = null;
        try { data = text ? JSON.parse(text) : null; } catch (error) { data = null; }
        return { status: response.status, data: data };
      });
    }).finally(function () { if (timer) clearTimeout(timer); });
  }

  function submitEmail(root, form) {
    var product = readProduct(root);
    var to = (form.querySelector('input[name="to"]') || {}).value || '';
    var fromName = (form.querySelector('input[name="from_name"]') || {}).value || '';
    var message = (form.querySelector('textarea[name="message"]') || {}).value || '';
    var honeypot = (form.querySelector('input[name="website"]') || {}).value || '';
    to = to.trim(); fromName = fromName.trim().slice(0, 80); message = message.trim().slice(0, 240);

    if (!EMAIL_RE.test(to)) {
      setStatus(root, 'Please check the email address.', 'error');
      var toInput = form.querySelector('input[name="to"]');
      if (toInput) toInput.focus();
      return;
    }
    if (honeypot) { setStatus(root, 'Sent.', 'ok'); return; } // bots: pretend

    var send = form.querySelector('.cap-share__send');
    if (send) { send.disabled = true; send.textContent = 'Sending…'; }
    setStatus(root, 'Sending…', 'muted', true);

    var fallback = function (reason) {
      emit('email', 'mailto', { reason: reason });
      setStatus(root, 'Opening your email app with the link…', 'muted', true);
      try { window.location.href = mailtoFor(product, to, fromName, message); } catch (error) { /* ignore */ }
      if (send) { send.disabled = false; send.textContent = 'Send'; }
    };

    postShare(product.endpoint, {
      to: to,
      from_name: fromName,
      message: message,
      website: honeypot,
      locale: product.locale,
      product: { title: product.title, url: product.url, image: product.image, price: product.price }
    }).then(function (result) {
      if (send) { send.disabled = false; send.textContent = 'Send'; }
      if (result.status === 200 && result.data && result.data.ok) {
        emit('email', 'sent');
        form.reset();
        hideEmailForm(root);
        setStatus(root, 'Sent to ' + to + '.', 'ok');
        return;
      }
      if (result.status === 429 || (result.data && result.data.error === 'rate_limited')) {
        emit('email', 'rate_limited');
        setStatus(root, 'Too many shares in a row - please try again in a minute.', 'error', true);
        return;
      }
      if (result.status === 400 && result.data && result.data.error === 'invalid_email') {
        setStatus(root, 'Please check the email address.', 'error');
        return;
      }
      // { ok:false, fallback:'mailto' } = no email provider configured yet, or the
      // provider refused: open the shopper's own mail app with the link instead.
      fallback(result.data && result.data.error ? String(result.data.error) : 'http_' + result.status);
    }).catch(function (error) {
      fallback(error && error.name === 'AbortError' ? 'timeout' : 'network');
    });
  }

  // ---- copy / native share --------------------------------------------------
  function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text).catch(function () { return legacyCopy(text); });
    }
    return legacyCopy(text);
  }

  function legacyCopy(text) {
    return new Promise(function (resolve, reject) {
      try {
        var ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none';
        document.body.appendChild(ta);
        ta.select();
        var ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (ok) resolve(); else reject(new Error('copy failed'));
      } catch (error) { reject(error); }
    });
  }

  function canNativeShare() {
    return typeof navigator.share === 'function' && (navigator.maxTouchPoints > 0 || /Android|iPhone|iPad|iPod/i.test(navigator.userAgent));
  }

  // fallbackHref: where to go when the sheet is unavailable or fails (Facebook:
  // the sharer page); without it the link is copied with a hint instead.
  function nativeShare(root, network, fallbackHref) {
    var product = readProduct(root);
    var fallback = function () {
      if (fallbackHref) {
        emit(network, 'open');
        var win = null;
        try { win = window.open(fallbackHref, '_blank', 'noopener'); } catch (error) { win = null; }
        if (win === null) { try { window.location.assign(fallbackHref); } catch (error) { /* ignore */ } }
        return;
      }
      copyWithHint(root, product, network);
    };
    if (!canNativeShare()) { fallback(); return; }
    var sharing;
    try {
      sharing = navigator.share({ title: product.title, text: product.title + ' - Capucinne', url: product.url });
    } catch (error) { fallback(); return; }
    sharing.then(function () {
      emit(network, 'native_share');
      setStatus(root, 'Shared.', 'ok');
    }).catch(function (error) {
      if (error && error.name === 'AbortError') return; // shopper dismissed the sheet
      fallback();
    });
  }

  // Instagram: the share sheet wherever the browser has one (not only touch
  // devices - Chrome/Edge/Safari desktops offer it too); the row's own href
  // (the store's Instagram profile) is the fallback. Nothing is ever copied.
  function shareInstagram(root, href) {
    var product = readProduct(root);
    var fallback = function () {
      if (!href) return;
      emit('instagram', 'open');
      var win = null;
      try { win = window.open(href, '_blank', 'noopener'); } catch (error) { win = null; }
      if (win === null) { try { window.location.assign(href); } catch (error) { /* ignore */ } }
    };
    if (typeof navigator.share !== 'function') { fallback(); return; }
    var sharing;
    try {
      sharing = navigator.share({ title: product.title, text: product.title + ' - Capucinne', url: product.url });
    } catch (error) { fallback(); return; }
    sharing.then(function () {
      emit('instagram', 'native_share');
      setStatus(root, 'Shared.', 'ok');
    }).catch(function (error) {
      if (error && error.name === 'AbortError') return; // shopper dismissed the sheet
      fallback();
    });
  }

  function copyWithHint(root, product, network) {
    copyText(product.url).then(function () {
      emit(network, 'copied');
      setStatus(root, 'Link copied.', 'ok');
    }).catch(function () {
      emit(network, 'copy_failed');
      setStatus(root, 'Copy this link: ' + product.url, 'muted', true);
    });
  }

  // ---- delegated events ----------------------------------------------------
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    var trigger = target.closest('[data-cap-share-trigger]');
    if (trigger) {
      var root = trigger.closest(ROOT_SEL);
      if (!root) return;
      event.preventDefault();
      if (isOpen(root)) closeMenu(root, true); else openMenu(root);
      return;
    }

    var close = target.closest('[data-cap-share-close]');
    if (close) {
      event.preventDefault();
      closeMenu(close.closest(ROOT_SEL), true);
      return;
    }

    var item = target.closest('[data-cap-share-network]');
    if (item) {
      var itemRoot = item.closest(ROOT_SEL);
      if (!itemRoot) return;
      var network = item.getAttribute('data-cap-share-network');
      var product = readProduct(itemRoot);
      if (network === 'facebook' && canNativeShare()) {
        // Phones: the OS share sheet hands the piece straight to the Facebook
        // app (the sharer.php page opens facebook.com's mobile login wall in
        // Safari when the shopper is only signed in inside the app). If the
        // sheet is unavailable or fails, fall through to the sharer link.
        event.preventDefault();
        nativeShare(itemRoot, 'facebook', item.getAttribute('href'));
        return;
      }
      if (network === 'facebook' || network === 'pinterest' || network === 'whatsapp') {
        // Plain target=_blank links: the browser navigates, nothing is prevented
        // (popup blockers, iOS Safari and the theme-editor iframe all allow that).
        emit(network, 'open');
        return;
      }
      if (network === 'instagram') {
        // plain link to the store's Instagram profile unless a share sheet exists
        if (typeof navigator.share !== 'function') { emit('instagram', 'open'); return; }
        event.preventDefault();
        shareInstagram(itemRoot, item.getAttribute('href'));
        return;
      }
      event.preventDefault();
      if (network === 'native') { nativeShare(itemRoot, 'native'); return; }
      if (network === 'copy') { copyWithHint(itemRoot, product, 'copy'); return; }
      if (network === 'email') { toggleEmailForm(itemRoot); return; }
      return;
    }

    // Click outside any open menu closes it.
    var openRoots = document.querySelectorAll(ROOT_SEL + '.' + OPEN_CLASS);
    for (var i = 0; i < openRoots.length; i += 1) {
      if (!openRoots[i].contains(target)) closeMenu(openRoots[i], false);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    var openRoots = document.querySelectorAll(ROOT_SEL + '.' + OPEN_CLASS);
    if (!openRoots.length) return;
    event.preventDefault();
    for (var i = 0; i < openRoots.length; i += 1) closeMenu(openRoots[i], true);
  });

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof Element) || !form.matches('[data-cap-share-email]')) return;
    event.preventDefault();
    var root = form.closest(ROOT_SEL);
    if (root) submitEmail(root, form);
  });

  // Expose the fallback builder for probes.
  window.CapucinneShare = { mailtoFor: mailtoFor };
})();
